<!--
 * Path: packages/frontend/src/features/browser/forms/timeline/Timeline.vue
 * Task: Form wrapper for the Timeline relation presentation.
-->
<template>
  <FormBlockWrapper
    :is-editing="isEditing"
    @update:is-editing="handleToggleEdit"
    :title="field.name"
    :info="field.info"
    :can-edit="canEdit"
    :disable-edit="field.readonly"
    :is-saving="uiStore.loading.save"
    @save="saveChanges"
    @show-info="$emit('show-info')"
  >
    <template #read>
      <TimelineRead :field="field" />
    </template>
    <template #edit>
      <Multiselect
        :field="field"
        v-model="modelValueProxy"
        @show-info="$emit('show-info')"
        class="mb-0"
      />
    </template>
  </FormBlockWrapper>
</template>

<script setup lang="ts">
import { ref, computed, inject } from 'vue';
import { useWorkspaceStore } from '@/store/workspace';
import { useUiStore } from '@/store/ui';
import { useEntityOperations } from '@/core/composables/useEntityOperations';
import { usePermissions } from '@/core/composables/usePermissions';
import { FormContextKey } from '@/core/keys';
import FormBlockWrapper from '@/core/components/forms/FormBlockWrapper.vue';
import Multiselect from '@/core/components/forms/Multiselect.vue';
import TimelineRead from './TimelineRead.vue';
import type { Entity } from '@/core/types/editor';

const props = defineProps<{ field: any; errorMessages?: string | string[] }>();
const emit = defineEmits<{ (e: 'show-info'): void; }>();

const workspaceStore = useWorkspaceStore();
const uiStore = useUiStore();
const { saveEntity } = useEntityOperations();
const { canEditWorkspace } = usePermissions();
const formContext = inject(FormContextKey, null);

const isEditing = ref(false);
const canEdit = computed(() => canEditWorkspace.value && !props.field.readonly);
const activeEntity = computed<Entity | null>(() => formContext ? formContext.entity.value : workspaceStore.selectedEntity);

const handleToggleEdit = (newVal: boolean) => {
  if (!newVal) {
    workspaceStore.resetSelectedEntity(formContext?.isSecondary || false);
  }
  isEditing.value = newVal;
};

const saveChanges = async () => {
  await saveEntity(formContext?.isSecondary || false);
  isEditing.value = false;
};

const modelValueProxy = computed({
  get: () => {
    if (!activeEntity.value || !props.field.id) return [];
    return activeEntity.value[props.field.id] || [];
  },
  set: (newValue: any[]) => {
    if (activeEntity.value && props.field.id) {
      activeEntity.value[props.field.id] = newValue;
    }
  }
});
</script>
